import { FC, useEffect } from "react";
import { useDebugStore } from "@/shared/store/debugStore";
import { BreakpointProvider } from "@/shared/hooks/BreakpointProvider";
import { DeviceProvider } from "@/shared/hooks/DeviceProvider";
import { DebugProvider } from "@/shared/hooks/DebugProvider";
import { useThemeStore } from "@/store/themeStore";
import { OpenFeatureProvider } from "@openfeature/react-sdk";
import { Link } from "@tanstack/react-router";
import { CultureProvider, DialogProvider, ThemeProvider, ToastProvider } from "@vintecc/components";
import { FeatureFlagGate } from "./FeatureFlagGate";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/queryClient";

import { appI18n } from "@/services/culture/i18n";
import { ModuleTranslationsContext } from "@/services/culture/moduleTranslations";
import type { InitialCulture } from "@/services/culture/bootstrap";
import { useCultureProviderState } from "@/services/culture/useCultureProviderState";
import { initializeFlipt } from "@/helpers/flipt";
import { ENV } from "@/env";

type ProvidersProps = {
  children: React.ReactNode;
  initialCulture?: InitialCulture;
};

initializeFlipt();

/**
 * Wraps the app with all necessary providers (theme, toast, feature flags, etc.)
 * Called ClientProviders in case SSR providers are needed in the future, but currently only contains client-side providers.
 */
export const ClientProviders: FC<ProvidersProps> = ({ children, initialCulture }) => {
  const { currentThemeConfig } = useThemeStore();
  const { locale, translations, isCommonReady, ensureModuleLoaded, isModuleLoaded } = useCultureProviderState(initialCulture);
  const showKeys = useDebugStore((s) => s.showKeys);

  // Initialize debugStore from query param on first mount
  useEffect(() => {
    useDebugStore.getState().initializeFromQuery();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={currentThemeConfig} linkComponent={Link}>
        <ModuleTranslationsContext.Provider value={{ ensureModuleLoaded, isModuleLoaded }}>
          <CultureProvider locale={locale} translations={translations} i18n={appI18n} showKeys={showKeys}>
            {isCommonReady ? (
              <BreakpointProvider>
                <DeviceProvider>
                  <DialogProvider>
                    <ToastProvider>
                      <OpenFeatureProvider>
                        <DebugProvider enabled={ENV.DEBUG_MODAL_ENABLED} variables={{ locale, ...ENV }}>
                          <FeatureFlagGate>{children}</FeatureFlagGate>
                        </DebugProvider>
                      </OpenFeatureProvider>
                    </ToastProvider>
                  </DialogProvider>
                </DeviceProvider>
              </BreakpointProvider>
            ) : (
              <div className="min-h-screen flex items-center justify-center text-sm text-grey-500">Loading...</div>
            )}
          </CultureProvider>
        </ModuleTranslationsContext.Provider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};
